import { TStoryFrame } from 'context/StudioEditorProvider';
import { TStorySnapshot, TStoryVersionedFrame } from 'types/story';
import { TStoryLocalizedItemsWire } from 'types/storyWire';

import { itemContent } from './divergence';
import { toWireItems } from './wire';

/**
 * Stamps each item with a version describing how many content revisions of that slide have
 * shipped: `v` answers "does this slide differ from what app users have already seen".
 *
 * The value is *derived* from the live snapshot rather than incremented, which makes it
 * idempotent — saving the same draft ten times produces the same `v`, and editing a slide back
 * to match live drops the bump again. That is what keeps versions from inflating across the
 * many draft saves that happen before a publish.
 *
 * Reordering is not a change: slides are matched by `id`, and order belongs to the group.
 */
export const resolveItemVersions = (
  frames: TStoryFrame[],
  live: TStorySnapshot | null
): TStoryLocalizedItemsWire => {
  const liveById = new Map((live?.items ?? []).map((item) => [item.id, item]));

  /*
   * `v` is derived on the EDITOR frame, before expansion, and `toWireItems` then replicates it into
   * every locale bucket — one slide cannot sit at two revisions at once. `itemContent` hashes the
   * frame across all its locales, so a translation-only edit still bumps it.
   */
  const stamped: TStoryVersionedFrame[] = frames.map((frame) => {
    const liveItem = liveById.get(frame.id);
    if (!liveItem) {
      return { ...frame, v: 1 };
    }
    const shipped = liveItem.v ?? 1;
    return { ...frame, v: itemContent(frame) === itemContent(liveItem) ? shipped : shipped + 1 };
  });

  return toWireItems(stamped);
};
